import {
  type DefaultOptions,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { type JSX, type ReactNode, useMemo } from 'react'
import { cacheIndividualResources } from '#src/cache.ts'
import { useSyncCache } from '#src/hooks/useSyncCache.ts'

export function QueryProvider(props: {
  defaultQueryOptions?: DefaultOptions['queries']
  children: ReactNode
}): JSX.Element {
  const queryClient = useMemo(
    () => getQueryClient(props.defaultQueryOptions),
    [props.defaultQueryOptions]
  )

  useSyncCache(queryClient)

  return (
    <QueryClientProvider client={queryClient}>
      {props.children}
    </QueryClientProvider>
  )
}

function getQueryClient(
  defaultQueryOptions?: DefaultOptions['queries']
): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 2, // Will retry failed requests 2 times
        retryDelay: (attemptIndex) => Math.min(250 * 2 ** attemptIndex, 30000), // Exponential backoff
        ...defaultQueryOptions,
      },
      mutations: {
        onError: (error) => {
          console.error('Mutation error:', error)
        },
      },
    },
  })
  const cache = client.getQueryCache()
  cache.subscribe((event) => {
    if (!(event.type === 'added' || event.type === 'updated')) {
      return
    }
    cacheIndividualResources(event.query, client)
  })
  return client
}
